import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import {
  MonitoreoRelacionFincaCultivoPlagaItem,
  MonitoreoRelacionFincaCultivoPlagaService,
} from "../../../../generated/grpc/configuracion_monitoreo_pb";
import { grpcMonitoreoRelacionFincaCultivoPlagaDatabase } from "../database/GrpcMonitoreoRelacionFincaCultivoPlagaDatabase";
import { serviceSync } from "../../../../base/services/ServiceSync";
import { MonitoreoRelacionFincaCultivoPlagaResponse } from "../response/MonitoreoRelacionFincaCultivoPlagaResponse";

/** Variante gRPC-Web (con delta real, por finca) de
 *  `MonitoreoRelacionFincaCultivoPlagaGetListByFincaIdService` (ver ese
 *  archivo). Devuelve `MonitoreoRelacionFincaCultivoPlagaResponse[]` -mismo
 *  shape que ya usa el resto de la app-. Solo expone el listado por finca: el
 *  global (no scoped) no lo consume el Cliente y sigue solo en REST. */
class MonitoreoRelacionFincaCultivoPlagaGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<MonitoreoRelacionFincaCultivoPlagaResponse[]> {
    await this._syncAsync();
    const items = await grpcMonitoreoRelacionFincaCultivoPlagaDatabase.getListByFincaIdAsync<MonitoreoRelacionFincaCultivoPlagaItem>(
      this._fincaId,
    );
    return items.map(toMonitoreoRelacionFincaCultivoPlagaResponse);
  }

  clearCache(): void {
    serviceSync.reset(`grpc:monitoreo-relacion-plaga:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:monitoreo-relacion-plaga:${this._fincaId}`, async () => {
      const ultimaSincronizacion =
        await grpcMonitoreoRelacionFincaCultivoPlagaDatabase.getUltimaSincronizacionAsync(
          this._fincaId,
        );
      const client = createClient(
        MonitoreoRelacionFincaCultivoPlagaService,
        grpcTransportProvider.get(),
      );

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcMonitoreoRelacionFincaCultivoPlagaDatabase.putDeltaAsync(
        this._fincaId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createMonitoreoRelacionFincaCultivoPlagaGetListByFincaIdService`). */
export const fx_createMonitoreoRelacionFincaCultivoPlagaGrpcService = createCachedFactory(
  (fincaId: string) => new MonitoreoRelacionFincaCultivoPlagaGrpcService(fincaId),
);

/** Adapta un `MonitoreoRelacionFincaCultivoPlagaItem` (gRPC) al shape
 *  `MonitoreoRelacionFincaCultivoPlagaResponse` (REST). Exportada porque los
 *  servicios de guardado/borrado también la necesitan. `clienteId`,
 *  `cultivoNombre` y `plagaEnfermedadNombreTecnico` se rellenan inertes: nadie
 *  los lee en el Cliente (auditado; ver comentario del proto). */
export function toMonitoreoRelacionFincaCultivoPlagaResponse(
  relacion: MonitoreoRelacionFincaCultivoPlagaItem,
): MonitoreoRelacionFincaCultivoPlagaResponse {
  return {
    id: relacion.id,
    isEliminado: relacion.eliminado,
    clienteId: "",
    fincaId: relacion.fincaId,
    cultivoId: relacion.cultivoId,
    cultivoNombre: "",
    plagaEnfermedadId: relacion.plagaEnfermedadId,
    plagaEnfermedadNombreComun: relacion.plagaEnfermedadNombreComun,
    plagaEnfermedadNombreTecnico: "",
    nivelBajoMax: relacion.nivelBajoMax,
    nivelMedioMax: relacion.nivelMedioMax,
    incidenciaBajoMax: relacion.incidenciaBajoMax,
    incidenciaMedioMax: relacion.incidenciaMedioMax,
    referenciaVersion: relacion.referenciaVersion,
    referenciaModificadoPor: relacion.referenciaModificadoPor,
    referenciaModificadoEn: relacion.referenciaModificadoEn
      ? Number(relacion.referenciaModificadoEn)
      : undefined,
  };
}
